import React from "react";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";

const ArrowIcon = ({ direction = "right", double = false, label }) => {
  const transform = direction === "left" ? "rotate(180 12 12)" : undefined;

  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      role="img"
      focusable="false"
    >
      <title>{label}</title>
      <g transform={transform}>
        <polyline points="10 6 16 12 10 18" />
        {double && <polyline points="6 6 12 12 6 18" />}
      </g>
    </svg>
  );
};

const Pagination = ({
  totalItems,
  itemsPerPage,
  currentPage,
  onPageChange,
  onItemsPerPageChange,
  itemsPerPageOptions = [10, 20, 30, 50, 100],
  hasMore = false,
}) => {
  const { buttonTableStyle } = useButtonTableStyle();
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage) || 1);
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);
  const canGoNext = hasMore || currentPage < totalPages;
  const canGoLast = !hasMore && currentPage < totalPages;

  const handleFirstPage = () => {
    if (currentPage > 1) {
      onPageChange(1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (canGoNext) {
      onPageChange(currentPage + 1);
    }
  };

  const handleLastPage = () => {
    if (canGoLast) {
      onPageChange(totalPages);
    }
  };

  const handleItemsPerPageChange = (e) => {
    const newItemsPerPage = parseInt(e.target.value, 10);
    onItemsPerPageChange(newItemsPerPage);
    // Reset to first page when changing items per page
    onPageChange(1);
  };

  if (totalItems === 0) {
    return null;
  }

  return (
    <div
      className="d-flex justify-content-between align-items-center app-table-pagination"
      style={{
        padding: "0.75rem 1rem",
        backgroundColor: "#f8f9fa",
        borderRadius: buttonTableStyle === "square" ? "0" : "0.375rem",
        marginTop: "1rem",
      }}
    >
      {/* Left side: Rows per page selector */}
      <div className="d-flex align-items-center gap-2">
        <span
          style={{
            fontSize: "0.875rem",
            color: "#6c757d",
            whiteSpace: "nowrap",
          }}
        >
          Rows per page:
        </span>
        <select
          className="form-select form-select-sm"
          value={itemsPerPage}
          onChange={handleItemsPerPageChange}
          style={{
            width: "auto",
            minWidth: "70px",
            fontSize: "0.875rem",
            backgroundColor: "#fff",
            border: "1px solid #dee2e6",
            borderRadius: buttonTableStyle === "square" ? "0" : "0.25rem",
            padding: "0.25rem 2rem 0.25rem 0.5rem",
            cursor: "pointer",
          }}
        >
          {itemsPerPageOptions.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>

      {/* Right side: Range display and navigation buttons */}
      <div className="d-flex align-items-center gap-3">
        {/* Range display */}
        <span
          style={{
            fontSize: "0.875rem",
            color: "#6c757d",
            whiteSpace: "nowrap",
          }}
        >
          {startItem}-{endItem} of {hasMore ? `${endItem}+` : totalItems}
        </span>

        {/* Navigation buttons */}
        <div className="d-flex align-items-center gap-1">
          {/* First page button */}
          <button
            type="button"
            className="btn btn-sm"
            onClick={handleFirstPage}
            disabled={currentPage === 1}
            style={{
              padding: "0.25rem 0.5rem",
              border: "1px solid #dee2e6",
              backgroundColor: currentPage === 1 ? "#f8f9fa" : "#fff",
              color: currentPage === 1 ? "#6c757d" : "#212529",
              cursor: currentPage === 1 ? "not-allowed" : "pointer",
              borderRadius: buttonTableStyle === "square" ? "0" : "0.25rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: "32px",
              height: "32px",
            }}
            title="First page"
          >
            <ArrowIcon direction="left" double label="First page" />
          </button>

          {/* Previous page button */}
          <button
            type="button"
            className="btn btn-sm"
            onClick={handlePrevPage}
            disabled={currentPage === 1}
            style={{
              padding: "0.25rem 0.5rem",
              border: "1px solid #dee2e6",
              backgroundColor: currentPage === 1 ? "#f8f9fa" : "#fff",
              color: currentPage === 1 ? "#6c757d" : "#212529",
              cursor: currentPage === 1 ? "not-allowed" : "pointer",
              borderRadius: buttonTableStyle === "square" ? "0" : "0.25rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: "32px",
              height: "32px",
            }}
            title="Previous page"
          >
            <ArrowIcon direction="left" label="Previous page" />
          </button>

          {/* Next page button */}
          <button
            type="button"
            className="btn btn-sm"
            onClick={handleNextPage}
            disabled={!canGoNext}
            style={{
              padding: "0.25rem 0.5rem",
              border: "1px solid #dee2e6",
              backgroundColor: !canGoNext ? "#f8f9fa" : "#fff",
              color: !canGoNext ? "#6c757d" : "#212529",
              cursor: !canGoNext ? "not-allowed" : "pointer",
              borderRadius: buttonTableStyle === "square" ? "0" : "0.25rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: "32px",
              height: "32px",
            }}
            title="Next page"
          >
            <ArrowIcon label="Next page" />
          </button>

          {/* Last page button */}
          <button
            type="button"
            className="btn btn-sm"
            onClick={handleLastPage}
            disabled={!canGoLast}
            style={{
              padding: "0.25rem 0.5rem",
              border: "1px solid #dee2e6",
              backgroundColor: !canGoLast ? "#f8f9fa" : "#fff",
              color: !canGoLast ? "#6c757d" : "#212529",
              cursor: !canGoLast ? "not-allowed" : "pointer",
              borderRadius: buttonTableStyle === "square" ? "0" : "0.25rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: "32px",
              height: "32px",
            }}
            title="Last page"
          >
            <ArrowIcon double label="Last page" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Pagination;
